import React, { useEffect } from 'react'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { Chart as ChartJS, registerables, defaults } from 'chart.js'

import { CssBaseline, ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Auth from 'App/Auth'

import LiveChat from 'components_new/organisms/LiveChat'

import AdminRoutes from './Routes/Admin'
import AnalysisRoutes from './Routes/Analyses'
import ApplicationRoutes from './Routes/Applications'
import TemplatesRoutes from './Routes/Templates'
import DashboardRoutes from './Routes/Dashboards'
import DataPlatformRoutes from './Routes/DataPlatform'
import ExportTablesRoutes from './Routes/ExportTables'
import GroupRoutes from './Routes/Groups'
import HomeRoutes from './Routes/Home'
import KeyMetricRoutes from './Routes/KeyMetrics'
import TaskRoutes from './Routes/Tasks'
import ListRoutes from './Routes/Lists'
import InternalRoutes from './Routes/Internal'
import McpRoutes from './Routes/Mcp'
import MetricRoutes from './Routes/Metrics'
import ReportRoutes from './Routes/Reports'
import ResourceRoutes from './Routes/Resources'

import { useStatusBannerActions } from 'redux/actions/StatusBanner'
import { useSelfActions } from 'redux/actions/Self'

import EmbeddedWidget from 'components_new/pages/EmbeddedWidget'
import PublicDashboard from 'components_new/pages/PublicDashboard'
import PublicMetric from 'components_new/pages/PublicMetric'
import ApiAccessDashboard from 'components_new/pages/ApiAccessDashboard'
import ApiAccessWidget from 'components_new/pages/ApiAccessWidget'
import FortnoxActivation from 'components_new/pages/FortnoxActivation'
import VismaActivation from 'components_new/pages/VismaActivation'
import DynamicsActivation from 'components_new/pages/DynamicsActivation'
import PublicSetCredentials from 'components_new/pages/PublicSetCredentials'

import GlobalLayout from 'components_new/templates/GlobalLayout'

import RedirectWrapper from './RedirectWrapper'
import SnackbarWrapper from './SnackbarWrapper'

import NewUserDialog from 'components_new/organisms/dialogs/NewUserDialog'
import NpsSurvey from 'components_new/organisms/dialogs/NpsSurvey'
import AnalysisCompletedNotifier from 'components_new/organisms/AnalysisCompletedNotifier'

import { STATUS_BANNER_TIMER } from 'utils/constants'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useChat, useCustomer } from 'redux/hooks/Customer'
import { useAllowAccessAnalyses } from 'redux/hooks/Permissions'

import { LanguageProvider } from 'language/translator'

ChartJS.register(...registerables)
defaults.font.family = '\'Source Sans 3\', sans-serif'
defaults.color = 'rgba(26, 26, 29, 0.6)' // text.secondary

const App = () => {
  const { account } = useAccountSettings()
  const customer = useCustomer()
  const { tryGetStatusBanner } = useStatusBannerActions()
  const { tryUpdateSelf } = useSelfActions()
  const router = React.createRef()
  const theme = getTheme('light', null, { responsive: true })
  const showChat = useChat()
  const allowAccessAnalyses = useAllowAccessAnalyses()
  const newUserDialogOpen = Boolean(account && !account.has_finished_onboarding)

  useEffect(() => {
    // status banner for public pages are controlled in their pages
    if (customer && !customer.is_demo) {
      tryGetStatusBanner()

      const interval = setInterval(
        () => tryGetStatusBanner(),
        STATUS_BANNER_TIMER
      )

      return () => clearInterval(interval)
    }

    return () => null
  }, [customer?.customer_id])

  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <CssBaseline enableColorScheme={true} />
        <Router ref={router}>
          <Route path={'/public/widgets/:id'} exact>
            <EmbeddedWidget />
          </Route>
          <Route path={'/public/dashboards/:id'} exact>
            <PublicDashboard />
          </Route>
          <Route path={'/public/metrics/:id'} exact>
            <PublicMetric />
          </Route>
          <Route path={'/public/fortnox-activation'} exact>
            <FortnoxActivation />
          </Route>
          <Route path={'/public/visma-activation'} exact>
            <VismaActivation />
          </Route>
          <Route path={'/public/dynamics-activation'} exact>
            <DynamicsActivation />
          </Route>
          <Route path={'/public/credentials/:uniqueString'} exact>
            <PublicSetCredentials />
          </Route>
          <Route path={'/api-access/dashboards/:id'} exact>
            <ApiAccessDashboard />
          </Route>
          <Route path={'/api-access/widgets/:id'} exact>
            <ApiAccessWidget />
          </Route>
          {window.location.pathname.includes('/public/') ||
          window.location.pathname.includes('/api-access/') ? null : (
              <Auth>
                <RedirectWrapper>
                  <GlobalLayout>
                    {showChat ? (
                      <LiveChat
                        user={{
                          email: account.email,
                          id: account.id,
                          name: `${account.first_name} ${account.last_name}`
                        }}
                      />
                    ) : null}

                    <ApplicationRoutes />
                    <AdminRoutes />
                    <HomeRoutes />
                    <DashboardRoutes />
                    <GroupRoutes />
                    <McpRoutes />
                    <MetricRoutes />
                    <KeyMetricRoutes />
                    <TaskRoutes />
                    <AnalysisRoutes />
                    <TemplatesRoutes />
                    <ExportTablesRoutes />
                    <InternalRoutes />
                    <DataPlatformRoutes />
                    <ListRoutes />
                    <ReportRoutes />
                    <ResourceRoutes />

                    {/*-- dialogs --*/}
                    <NewUserDialog
                      onClose={() =>
                        tryUpdateSelf(
                          {
                            data: {
                              attributes: {
                                has_finished_onboarding: true
                              }
                            }
                          },
                          false
                        )
                      }
                      open={newUserDialogOpen}
                    />
                    <NpsSurvey />
                  </GlobalLayout>
                </RedirectWrapper>
                <SnackbarWrapper>
                  {allowAccessAnalyses ? <AnalysisCompletedNotifier /> : null}
                </SnackbarWrapper>
              </Auth>
            )}
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  )
}

export default App
